import { ORG_PLAN } from "../services/org/org.const"
import { TRIALS_RESET_AT } from "../services/billing/billing.const"

/**
 * Commercial tier of an org. Mirrors orgLicense.getOrgPlan() in the API: `plan` is the
 * source of truth, but orgs from before it existed have it unset and fall back to the
 * legacy flags - isVerified doubled as the Enterprise marker, isLicensed meant a (Stripe
 * or manual, e.g. AppSumo) Pro license. Keep the two in sync.
 *
 * One deliberate difference: here the legacy fallback also needs isLicensedActive, since
 * an expired trial leaves isLicensed behind and the billing screens would show Pro while
 * every Pro feature (gated on isLicensedActive) is off. The API's callers check the
 * Stripe status on top, so they don't need it.
 * @param {object} orgData
 * @returns {string} One of ORG_PLAN
 */
export const getOrgPlan = (orgData) => {
    if (orgData?.plan) {
        return orgData.plan
    }
    if (orgData?.isVerified === true) {
        return ORG_PLAN.ENTERPRISE
    }
    if (orgData?.isLicensed === true && orgData?.isLicensedActive === true) {
        return ORG_PLAN.PRO
    }
    return ORG_PLAN.FREE
}

/**
 * Enterprise orgs are invoiced outside Stripe and licensed manually. Use this, not
 * orgData.isVerified, for anything plan-related - isVerified is only the org badge.
 * @param {object} orgData
 * @returns {boolean}
 */
export const isOrgEnterprise = (orgData) => getOrgPlan(orgData) === ORG_PLAN.ENTERPRISE

/**
 * Whether the org is on an AppSumo lifetime deal. `plan_id` (not `appSumoId`) is the
 * marker the whole codebase uses - it is only written once a tier has actually been
 * activated - and it mirrors orgLicense.isOrgAppSumo() in the API, which is what
 * actually enforces the reduced add-on allowance (APP_SUMO_FREE_TIER_QUOTA).
 *
 * Reads false for anyone below Org Admin: `appSumo` is in canReadOrgAllowedFieldsAdmin()
 * only, so it never reaches a manager's or member's orgData. That is fine for every
 * caller here - the billing pages are admin-only, and the quota itself is enforced
 * server-side whatever the UI shows.
 * @param {object} orgData
 * @returns {boolean}
 */
export const isOrgAppSumo = (orgData) => !!orgData?.appSumo?.plan_id

/**
 * Whether an org or person started its trial of `product` since TRIALS_RESET_AT. Only
 * trialsUsedAt carries a date, so the older markers (trialsUsed booleans, hasTrialed,
 * trialStartedOn, stripe.trialEnds, hasTrialedOrg, a lapsed licence) don't count.
 *
 * @param {object} doc orgData or app.state.person
 * @param {string} product One of TRIAL_PRODUCT
 * @returns {boolean}
 */
const hasUsedTrialSinceReset = (doc, product) => doc?.trialsUsedAt?.[product] >= TRIALS_RESET_AT

/**
 * Whether this org has already had its one trial of `product`. Mirrors
 * orgLicense.hasOrgUsedTrial() in the API, which is what actually refuses a second one.
 *
 * @param {object} orgData
 * @param {string} product One of TRIAL_PRODUCT
 * @returns {boolean}
 */
export const hasOrgUsedTrial = (orgData, product) => hasUsedTrialSinceReset(orgData, product)

/**
 * Whether this person has already started their one trial of `product`, in any org.
 * Mirrors orgLicense.hasPersonUsedTrial() in the API.
 *
 * @param {object} person app.state.person
 * @param {string} product One of TRIAL_PRODUCT
 * @returns {boolean}
 */
export const hasPersonUsedTrial = (person, product) => hasUsedTrialSinceReset(person, product)

/**
 * Whether the trial of `product` is still on offer here: neither this org nor this
 * person has had it. Every trial is one per org AND one per person, so a second org
 * created by the same admin gets no second Pro trial, and an org whose previous admin
 * trialed Purchasing cannot trial it again under a new one.
 *
 * @param {object} orgData
 * @param {object} person app.state.person
 * @param {string} product One of TRIAL_PRODUCT
 * @returns {boolean}
 */
export const canStartTrial = (orgData, person, product) =>
    !hasOrgUsedTrial(orgData, product) && !hasPersonUsedTrial(person, product)
